import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical } from 'lucide-react';

import {
  getShowDemoData,
  isDemoRecord,
  setShowDemoData,
  subscribeShowDemoData,
} from '@/utils/demoData';

/**
 * Admin-only chip marking a record that came from the store-review seed.
 * Renders nothing for real data, so it is safe to drop into any row.
 */
export const DemoBadge = ({ record, className = '' }) => {
  const { t } = useTranslation();

  if (!isDemoRecord(record)) return null;

  return (
    <span
      className={`bg-amber-100 text-amber-700 border border-amber-300 text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shrink-0 ${className}`}
      title={t('admin_demo_data_tooltip')}
      data-testid="demo-badge"
    >
      <FlaskConical className="w-3 h-3" />
      {t('admin_demo_data_badge')}
    </span>
  );
};

/**
 * Shared "show demo data" preference. Every admin surface that calls this hook
 * stays in sync, including across browser tabs.
 */
export function useShowDemoData() {
  const [showDemoData, setShow] = useState(getShowDemoData);

  useEffect(() => subscribeShowDemoData(setShow), []);

  return [showDemoData, setShowDemoData];
}

/** Toggle for the admin header / filter panels. */
export const DemoDataToggle = ({ demoCount = 0, className = '' }) => {
  const { t } = useTranslation();
  const [showDemoData, setShow] = useShowDemoData();

  return (
    <label
      className={`flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none ${className}`}
      title={t('admin_demo_data_tooltip')}
    >
      <input
        type="checkbox"
        className="h-3.5 w-3.5 accent-amber-500 cursor-pointer"
        checked={showDemoData}
        onChange={(e) => setShow(e.target.checked)}
        data-testid="demo-data-toggle"
      />
      <FlaskConical className="w-3.5 h-3.5 text-amber-600" />
      <span>{t('admin_demo_data_show')}</span>
      {demoCount > 0 && (
        <span className="bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-1.5 py-0.5 font-semibold">
          {demoCount}
        </span>
      )}
    </label>
  );
};

export default DemoBadge;
